import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MessageCircle } from 'lucide-react';
import { PlanDisplay } from './PlanCard';
import TherapistPlanCard from './TherapistPlanCard';
import { groupTherapistPlans } from './groupTherapistPlans';
import {
  CONSTELADOR_WHATSAPP,
  consteladorBenefits,
  consteladorPurchaseSteps,
} from '../../constants/consteladorLanding';

interface TherapistPlansLayoutProps {
  plans: PlanDisplay[];
  isLoading: boolean;
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  showBackLink?: boolean;
  /**
   * Quando true (padrão na página /planos?secao=terapeuta): preços no topo e
   * “como funciona” abaixo — mesmo padrão da página do constelado.
   * Na landing /constelador use false para não duplicar o conteúdo de venda.
   */
  showJourneyBelow?: boolean;
}

interface PlanSlotProps {
  plan: PlanDisplay;
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  className?: string;
  animationDelay?: string;
}

const PlanSlot: React.FC<PlanSlotProps> = ({
  plan,
  processingPlan,
  onSelect,
  className = '',
  animationDelay,
}) => (
  <div
    className={`plans-animate-in flex h-full flex-col ${className}`}
    style={animationDelay ? { animationDelay } : undefined}
  >
    <TherapistPlanCard
      plan={plan}
      onSelect={onSelect}
      isLoading={processingPlan === plan.id}
    />
  </div>
);

const HourPlansGrid: React.FC<{
  avulso: PlanDisplay | null;
  pacotes: PlanDisplay[];
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
}> = ({ avulso, pacotes, processingPlan, onSelect }) => {
  if (!avulso && pacotes.length === 0) return null;

  const pacoteCols =
    pacotes.length === 1
      ? 'max-w-md mx-auto'
      : pacotes.length === 2
        ? 'md:grid-cols-2 max-w-4xl mx-auto'
        : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';

  return (
    <div className="space-y-6 md:space-y-8">
      {avulso && (
        <PlanSlot
          plan={avulso}
          processingPlan={processingPlan}
          onSelect={onSelect}
          className="mx-auto w-full max-w-2xl"
          animationDelay="0.15s"
        />
      )}

      {pacotes.length > 0 && (
        <div className={`grid grid-cols-1 gap-6 md:gap-8 ${pacoteCols}`}>
          {pacotes.map((plan, index) => (
            <PlanSlot
              key={plan.id}
              plan={plan}
              processingPlan={processingPlan}
              onSelect={onSelect}
              animationDelay={`${0.2 + index * 0.06}s`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SubscriptionPlansGrid: React.FC<{
  plans: PlanDisplay[];
  processingPlan: string | null;
  onSelect: (id: string, quantity?: number) => void;
  baseDelay?: number;
}> = ({ plans, processingPlan, onSelect, baseDelay = 0.25 }) => {
  if (plans.length === 0) return null;

  const cols =
    plans.length === 1
      ? 'max-w-md mx-auto'
      : plans.length === 2
        ? 'md:grid-cols-2 max-w-4xl mx-auto'
        : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';

  return (
    <div className={`grid grid-cols-1 gap-6 md:gap-8 ${cols}`}>
      {plans.map((plan, index) => (
        <PlanSlot
          key={plan.id}
          plan={plan}
          processingPlan={processingPlan}
          onSelect={onSelect}
          animationDelay={`${baseDelay + index * 0.06}s`}
        />
      ))}
    </div>
  );
};

const TherapistPlansLayout: React.FC<TherapistPlansLayoutProps> = ({
  plans,
  isLoading,
  processingPlan,
  onSelect,
  showBackLink = true,
  showJourneyBelow = true,
}) => {
  const { avulso, pacotes, subscriptions } = groupTherapistPlans(plans);
  const hasHourSection = !!avulso || pacotes.length > 0;
  const hasSubscriptionSection = subscriptions.length > 0;

  const plansSection = (
    <section
      id="planos"
      className={`plans-animate-in plans-animate-delay-2 scroll-mt-28 ${
        showJourneyBelow ? 'mt-8' : 'mt-0'
      }`}
      aria-label="Planos disponíveis"
    >
      {showJourneyBelow && (
        <div className="mb-10 text-center">
          <p className="mx-auto max-w-3xl text-lg font-bold leading-relaxed text-[#2B5A41] sm:text-xl md:text-2xl">
            Escolha o <span className="text-[#2B5A41]">plano e o valor</span> para usar a plataforma
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div
            className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-[#89A78C] border-r-transparent"
            role="status"
            aria-label="Carregando planos"
          />
          <p className="mt-4 font-medium text-[#2B5A41]">Carregando planos...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#2B5A41]/30 bg-white py-16 text-center shadow-sm ring-1 ring-black/5">
          <p className="text-[#2B5A41]">Nenhum plano disponível no momento.</p>
        </div>
      ) : (
        <div className="space-y-10 md:space-y-12">
          {hasHourSection && (
            <HourPlansGrid
              avulso={avulso}
              pacotes={pacotes}
              processingPlan={processingPlan}
              onSelect={onSelect}
            />
          )}

          {hasSubscriptionSection && (
            <SubscriptionPlansGrid
              plans={subscriptions}
              processingPlan={processingPlan}
              onSelect={onSelect}
            />
          )}
        </div>
      )}
    </section>
  );

  const journeySection = showJourneyBelow ? (
    <section
      className="plans-animate-in mt-14 space-y-10"
      aria-labelledby="therapist-how-heading"
    >
      <div className="rounded-[1.75rem] border border-[#E0EDE3] bg-white p-8 shadow-[0_18px_40px_rgba(43,90,65,0.08)] md:p-10">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#2B5A41]">
          Como funciona
        </p>
        <h2
          id="therapist-how-heading"
          className="text-2xl font-bold text-[#2B5A41] sm:text-3xl"
        >
          Do plano à sua sala
        </h2>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-[#5F7167]">
          Contrate horas avulsas, pacotes ou assinatura com homologação. Após o pagamento, o acesso
          à plataforma é liberado na hora.
        </p>

        <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {consteladorPurchaseSteps.map((item) => (
            <li
              key={item.step}
              className="rounded-2xl border border-[#E0EDE3] bg-[#F5F6F0] p-5"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#2B5A41] text-sm font-bold text-white">
                {item.step}
              </span>
              <h3 className="mt-3 text-base font-bold text-[#2B5A41]">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5F7167]">{item.text}</p>
            </li>
          ))}
        </ol>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {consteladorBenefits.slice(0, 4).map((benefit) => (
            <article
              key={benefit.title}
              className="rounded-2xl border border-[#E0EDE3] bg-white p-5"
            >
              <h3 className="text-base font-bold text-[#2B5A41]">{benefit.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5F7167]">{benefit.text}</p>
            </article>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <a
            href="#planos"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2B5A41] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#203C2E]"
          >
            Ver planos acima
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
          <Link
            to="/constelador"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#2B5A41]/25 px-6 py-3 text-sm font-semibold text-[#2B5A41] transition hover:border-[#2B5A41]/45 hover:bg-[#E0EDE3]"
          >
            Conhecer a plataforma
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>

      <div className="rounded-[1.75rem] border border-[#E0EDE3] bg-white p-6 md:p-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="text-xl font-bold text-[#2B5A41]">Ainda com dúvidas?</h2>
            <p className="mt-2 text-sm text-[#5F7167]">
              Nossa equipe ajuda você a escolher o plano certo.
            </p>
          </div>
          <a
            href={CONSTELADOR_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20BA5A]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden />
            WhatsApp
          </a>
        </div>
      </div>
    </section>
  ) : null;

  return (
    <div className="mx-auto max-w-6xl px-1 sm:px-2">
      {showBackLink && (
        <Link
          to="/planos"
          className="plans-animate-in mb-8 inline-flex items-center gap-2 rounded-full border border-[#2B5A41]/20 bg-white/80 px-4 py-2 text-sm font-medium text-[#2B5A41] shadow-sm backdrop-blur-sm transition hover:border-[#2B5A41]/40 hover:bg-white hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Voltar para escolha de caminho
        </Link>
      )}

      {showJourneyBelow ? (
        <header className="plans-animate-in plans-animate-delay-1 mb-2 max-w-3xl">
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#2B5A41]">
            Sou constelador
          </p>
          <h1 className="text-3xl font-bold leading-tight text-[#2B5A41] md:text-4xl">
            Planos para Consteladores
          </h1>
          <p className="mt-3 text-lg leading-relaxed text-[#5F7167]">
            Veja os valores abaixo, escolha horas avulsas, pacote ou assinatura e comece a atender
            na plataforma.
          </p>
        </header>
      ) : null}

      {plansSection}
      {journeySection}
    </div>
  );
};

export default TherapistPlansLayout;
